import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';
import OptionButton from './OptionButton';

const styles = require('./OptionButton.module.scss');

type Context = 'talent' | 'company' | 'neutral';

interface Option {
  label: string;
  value: string;
}

export interface OptionButtonGroupProps {
  autoSaveUrl?: string;
  context?: Context;
  defaultSelected?: string[];
  hint?: string;
  inputName?: string;
  label?: string;
  mode: 'single' | 'multi';
  onChange?: (selected: string[]) => void;
  options: Option[];
  required?: boolean;
}

const OptionButtonGroup: React.FC<OptionButtonGroupProps> = ({
  autoSaveUrl,
  context = 'talent',
  defaultSelected = [],
  hint,
  inputName,
  label = 'Select an option',
  mode,
  onChange,
  options,
  required,
}) => {
  const { save } = useAutoSave({ url: autoSaveUrl || '', name: inputName || '', debounceMs: 0 });
  const optionValues = options.map(o => o.value);
  const [selected, setSelected] = useState<string[]>(
    defaultSelected.filter(v => optionValues.includes(v))
  );

  const handleChange = useCallback((value: string, isSelected: boolean) => {
    let next: string[];

    if (mode === 'single') {
      if (!isSelected) return;
      next = [value];
    } else {
      next = isSelected
        ? [...selected, value]
        : selected.filter(v => v !== value);
      if (next.length === 0) return;
    }

    setSelected(next);
    onChange?.(next);
    if (autoSaveUrl) save(mode === 'single' ? next[0] : next);
  }, [mode, onChange, selected, autoSaveUrl, save]);

  return (
    <div
      className={styles.wrapper}
      role={mode === 'single' ? 'radiogroup' : 'group'}
      aria-label={label}
      aria-required={required || undefined}
    >
      <p className={styles.title}>{label}</p>
      {inputName && selected.map(value => (
        <input key={value} type="hidden" name={inputName} value={value} />
      ))}
      <div className={classNames(styles.group, { [styles.vertical]: mode === 'single' })}>
        {options.map(option => (
          <OptionButton
            key={option.value}
            context={context}
            label={option.label}
            mode={mode}
            selected={selected.includes(option.value)}
            onChange={(isSelected) => handleChange(option.value, isSelected)}
          />
        ))}
      </div>
      {hint && (
        <div className={styles.hint}>
          <Icon name="info" size="sm" color="disable" aria-hidden="true" />
          <p>{hint}</p>
        </div>
      )}
    </div>
  );
};

export default OptionButtonGroup;
